/**
 * 页面能力矩阵（纯函数）：根据当前会话的 action 集合，推导每个页面的
 * 「分区加载计划」与「写按钮显隐」。页面渲染与 loader 都消费同一份推导结果，
 * 保证「无权限分区不请求、无权限按钮不展示」始终一致，也让权限矩阵可以用
 * 行为级单测直接验证（不依赖源码字符串搜索）。
 */

export type Actions = readonly string[];

/** 运行授权与商业计量页（入口 action：usage.read） */
export function runtimeCapabilities(actions: Actions) {
  const has = (action: string) => actions.includes(action);
  return {
    // 分区加载计划：缺对应权限的接口完全不请求，避免一项 403 拖垮整页
    loadServiceAccounts: has("runtime.manage"),
    loadPlanCatalog: has("plans.read"),
    loadSubscriptions: has("subscriptions.read"),
    loadTenants: has("tenant.read") && has("runtime.manage"), // 服务账号租户名称下拉/展示需要
    loadQuotaPolicy: has("quota.manage"), // 配额策略读取与写入同权限
    loadPrintPolicy: has("runtime.print_agent_policy.read"),
    // 写按钮
    showCreateAccount: has("runtime.manage"),
    showRotateAccount: has("runtime.manage"),
    showRevokeAccount: has("runtime.manage"),
    showCreateMeter: has("plans.manage"),
    showSaveQuota: has("quota.manage"),
    showSavePrintPolicy: has("runtime.print_agent_policy.write"),
  };
}

/** 连接与可靠投递页（入口 action：delivery.manage） */
export function integrationsCapabilities(actions: Actions) {
  const has = (action: string) => actions.includes(action);
  return {
    // 分区加载计划
    loadConnections: has("credential.read_metadata"), // GET /api/connections
    loadWebhooks: has("delivery.manage"),
    loadOutbox: has("delivery.manage"),
    // 写按钮
    showCreateConnection: has("connections.manage"), // 平台连接（创建/验证）
    showVerifyConnection: has("connections.manage"),
    showCreateWebhook: has("delivery.manage"), // 事件接收地址
    showRetryOutbox: has("delivery.manage"),
  };
}

/** 受控代管页（入口 action：tenant.read，只读可见） */
export function supportCapabilities(actions: Actions) {
  const has = (action: string) => actions.includes(action);
  return {
    showCreateGrant: has("support.grant"), // 申请代管
    showApprove: has("support.approve"), // 批准
    showActivate: has("support.activate"), // 激活
    showRevoke: has("support.revoke"), // 撤销
  };
}

/** 凭据保险库页（入口 action：credential.read_metadata） */
export function credentialsCapabilities(actions: Actions) {
  const has = (action: string) => actions.includes(action);
  return {
    showWrite: has("credential.write"), // 写入凭据
    showRotate: has("credential.rotate"), // 轮换
    showRevoke: has("credential.revoke"), // 吊销
    canReadTenants: has("tenant.read"), // 租户级凭据需要读取租户名单
  };
}

/** 平台管理员页（入口 action：admin.read） */
export function adminsCapabilities(actions: Actions, role: string) {
  const has = (action: string) => actions.includes(action);
  return {
    showCreate: has("admin.manage"), // 新增管理员
    showUpdate: has("admin.manage"), // 管理（角色/状态）
    showDelete: has("admin.manage") && role === "platform_owner", // 删除还需平台负责人
  };
}

/** 平台运营治理页（入口 action：health.read） */
export function operationsCapabilities(actions: Actions) {
  const has = (action: string) => actions.includes(action);
  return {
    // 分区加载计划：功能开关里的租户名称展示需要 tenant.read，缺失时整页仍可读
    loadTenantNames: has("tenant.read"),
    // 写按钮
    showIncidentActions: has("operations.manage"), // 登记事故、状态流转
    showMaintenanceActions: has("operations.manage"), // 维护窗口
    showFlagActions: has("feature_flags.manage"), // 功能开关新增/启停
  };
}

/** 订购与账单页（入口 action：orders.read） */
export function ordersBillingCapabilities(actions: Actions) {
  const has = (action: string) => actions.includes(action);
  return {
    loadOrdersBilling: has("orders.read"),
    showCreateOrder: has("orders.create"),
    showConfirmOfflinePayment: has("payments.confirm_offline"),
    showCancelOrder: has("orders.cancel"),
    showIssueInvoice: has("invoices.issue"),
    showRequestRefund: has("refunds.request"),
    showRunReconciliation: has("reconciliation.run"),
  };
}
